/*
 * BF catalog faceting — client-side.
 *
 * Every product card is rendered server-side and this script filters what is already in the
 * DOM. That is fine up to roughly 200 SKUs (one paginated page of 60 products today holds the
 * whole 31-product catalog). Past that, move to Shopify's Storefront Filtering API (native
 * collection filters / Search & Discovery) so filtering happens across all pages server-side.
 *
 * Semantics: within a group, ticked values are OR'd; across groups, AND'd. Certification is the
 * exception — ticked certifications AND together. Each option's count is the number of products
 * that would match if it were ticked, given every OTHER group's active filters.
 * Card values come only from data-* attributes (pipe-separated), never from visible text.
 */
(() => {
  const GROUPS = [
    { key: 'class', attr: 'class', mode: 'or' },
    { key: 'function', attr: 'function', mode: 'or' },
    { key: 'form', attr: 'form', mode: 'or' },
    // Most-held first so the near-universal ones don't bury the discriminating ones.
    { key: 'cert', attr: 'cert', mode: 'and', byCount: true },
    { key: 'hazard', attr: 'hazard', mode: 'or', order: ['Not classified', 'Warning'] },
    { key: 'price', attr: 'priceBand', mode: 'or', order: ['Under $5/lb', '$5–10/lb', '$10–25/lb', '$25+/lb'] },
  ];
  const SORTS = ['name', 'price-asc', 'price-desc', 'undercut'];

  const split = (s) => (s ? s.split('|').map((v) => v.trim()).filter(Boolean) : []);

  function init(root) {
    if (root.dataset.bfReady) return;
    root.dataset.bfReady = 'true';

    const grid = root.querySelector('[data-bf-grid]');
    const rail = root.querySelector('[data-bf-rail]');
    if (!grid || !rail) return;

    const cards = Array.from(grid.children).map((el) => {
      const vals = {};
      GROUPS.forEach((g) => (vals[g.key] = split(el.dataset[g.attr])));
      return {
        el,
        vals,
        title: el.dataset.title || '',
        minPrice: Number(el.dataset.minPrice) || 0,
        maxUndercut: Number(el.dataset.maxUndercut),
        tiers: Number(el.dataset.tiers) || 0,
      };
    });

    const state = { sel: {}, sort: 'name' };
    GROUPS.forEach((g) => (state.sel[g.key] = new Set()));

    // ---- read URL. One comma-separated param per group (?cert=Organic,Kosher&sort=undercut):
    // Shopify's myshopify.com -> primary-domain redirect keeps only the last of repeated keys,
    // which silently dropped filters from shared links. Repeated keys are still accepted on read.
    // No facet value contains a comma (checked against the payload).
    const params = new URLSearchParams(location.search);
    GROUPS.forEach((g) =>
      params.getAll(g.key).flatMap((v) => v.split(',')).map((v) => v.trim()).filter(Boolean)
        .forEach((v) => state.sel[g.key].add(v))
    );
    if (SORTS.includes(params.get('sort'))) state.sort = params.get('sort');

    // ---- build facet options from the cards themselves
    GROUPS.forEach((g) => {
      const fieldset = rail.querySelector(`[data-group="${g.key}"]`);
      const list = fieldset && fieldset.querySelector('[data-options]');
      if (!list) return;
      const values = new Set(cards.flatMap((c) => c.vals[g.key]));
      // byCount uses each value's total across the collection, not the live count, so the
      // list keeps a stable order while filters change.
      const total = (v) => cards.filter((c) => c.vals[g.key].includes(v)).length;
      const ordered = g.order
        ? g.order.filter((v) => values.has(v)).concat([...values].filter((v) => !g.order.includes(v)).sort())
        : g.byCount
          ? [...values].sort((a, b) => total(b) - total(a) || a.localeCompare(b))
          : [...values].sort((a, b) => a.localeCompare(b));

      // Drop URL values this collection doesn't have, and hide groups with nothing to offer.
      state.sel[g.key].forEach((v) => values.has(v) || state.sel[g.key].delete(v));
      fieldset.hidden = ordered.length === 0;

      ordered.forEach((value, i) => {
        const id = `${root.id}-${g.key}-${i}`;
        const li = document.createElement('li');
        li.className = 'bf-facet__option';
        li.innerHTML =
          `<input type="checkbox" id="${id}">` +
          `<label for="${id}"><span class="bf-facet__name"></span><span class="bf-facet__count" aria-hidden="true"></span></label>`;
        const input = li.querySelector('input');
        input.value = value;
        input.name = g.key;
        input.checked = state.sel[g.key].has(value);
        li.querySelector('.bf-facet__name').textContent = value;
        list.appendChild(li);
      });
    });

    const sortSelect = root.querySelector('[data-bf-sort]');
    if (sortSelect) sortSelect.value = state.sort;

    // ---- matching
    const groupMatches = (card, g, selected) => {
      if (!selected.size) return true;
      const have = card.vals[g.key];
      return g.mode === 'and' ? [...selected].every((v) => have.includes(v)) : have.some((v) => selected.has(v));
    };
    const matches = (card, exceptKey) =>
      GROUPS.every((g) => g.key === exceptKey || groupMatches(card, g, state.sel[g.key]));

    const comparators = {
      name: (a, b) => a.title.localeCompare(b.title),
      'price-asc': (a, b) => a.minPrice - b.minPrice || a.title.localeCompare(b.title),
      'price-desc': (a, b) => b.minPrice - a.minPrice || a.title.localeCompare(b.title),
      // Products whose tiers carry no percentage (maxUndercut -1) sort last.
      undercut: (a, b) => b.maxUndercut - a.maxUndercut || a.title.localeCompare(b.title),
    };

    const countEl = root.querySelector('[data-bf-count]');
    const chipsWrap = root.querySelector('[data-bf-chips]');
    const chipList = root.querySelector('[data-bf-chip-list]');
    const emptyEl = root.querySelector('[data-bf-empty]');
    const toggleCount = root.querySelector('[data-bf-toggle-count]');

    function render() {
      // Sort all cards, show matching ones.
      const sorted = cards.slice().sort(comparators[state.sort]);
      const visible = [];
      sorted.forEach((c) => {
        const on = matches(c, null);
        c.el.hidden = !on;
        if (on) visible.push(c);
        grid.appendChild(c.el);
      });

      // Counts per option, against every other group's filters.
      GROUPS.forEach((g) => {
        const base = cards.filter((c) => matches(c, g.key));
        rail.querySelectorAll(`input[name="${g.key}"]`).forEach((input) => {
          const v = input.value;
          const n = base.filter((c) => {
            if (g.mode === 'and') return [...state.sel[g.key], v].every((x) => c.vals[g.key].includes(x));
            return c.vals[g.key].includes(v);
          }).length;
          const label = input.nextElementSibling;
          label.querySelector('.bf-facet__count').textContent = n;
          input.disabled = n === 0 && !input.checked;
          input.setAttribute('aria-label', `${v}, ${n} ${n === 1 ? 'material' : 'materials'}`);
        });
      });

      // Summary line.
      const tiers = visible.reduce((sum, c) => sum + c.tiers, 0);
      if (countEl) {
        countEl.textContent = visible.length
          ? `${visible.length} ${visible.length === 1 ? 'material' : 'materials'} · ` +
            `${tiers} size ${tiers === 1 ? 'tier' : 'tiers'} in stock`
          : '0 materials';
      }

      // Chips.
      const active = GROUPS.flatMap((g) => [...state.sel[g.key]].map((v) => ({ g, v })));
      chipList.replaceChildren(
        ...active.map(({ g, v }) => {
          const li = document.createElement('li');
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'bf-chip';
          btn.setAttribute('aria-label', `Remove filter: ${v}`);
          btn.innerHTML = '<span></span><span class="bf-chip__x" aria-hidden="true">×</span>';
          btn.firstChild.textContent = v;
          btn.addEventListener('click', () => {
            state.sel[g.key].delete(v);
            const input = rail.querySelector(`input[name="${g.key}"][value="${CSS.escape(v)}"]`);
            if (input) input.checked = false;
            update();
          });
          li.appendChild(btn);
          return li;
        })
      );
      chipsWrap.hidden = active.length === 0;
      if (toggleCount) toggleCount.textContent = active.length ? `(${active.length})` : '';

      if (emptyEl) emptyEl.hidden = visible.length > 0;
    }

    function writeUrl() {
      const p = new URLSearchParams(location.search);
      GROUPS.forEach((g) => p.delete(g.key));
      p.delete('sort');
      GROUPS.forEach((g) => state.sel[g.key].size && p.set(g.key, [...state.sel[g.key]].join(',')));
      if (state.sort !== 'name') p.set('sort', state.sort);
      const qs = p.toString();
      history.replaceState(history.state, '', `${location.pathname}${qs ? `?${qs}` : ''}${location.hash}`);
    }

    function update() {
      render();
      writeUrl();
    }

    rail.addEventListener('change', (e) => {
      const input = e.target;
      if (input.type !== 'checkbox') return;
      const set = state.sel[input.name];
      if (!set) return;
      input.checked ? set.add(input.value) : set.delete(input.value);
      update();
    });

    sortSelect?.addEventListener('change', () => {
      state.sort = SORTS.includes(sortSelect.value) ? sortSelect.value : 'name';
      update();
    });

    root.querySelector('[data-bf-clear]')?.addEventListener('click', () => {
      GROUPS.forEach((g) => state.sel[g.key].clear());
      rail.querySelectorAll('input[type="checkbox"]').forEach((i) => (i.checked = false));
      update();
    });

    const toggle = root.querySelector('[data-bf-toggle]');
    toggle?.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', open);
      rail.classList.toggle('is-open', open);
    });

    render();
  }

  const run = () => document.querySelectorAll('[data-bf-catalog]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  document.addEventListener('shopify:section:load', run);
})();
